import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import type { Order, Product } from '@prody/shared';
import { OPEN_ORDER_STATUSES, computeOrderTotals } from '@prody/shared';
import {
  Button, Code, DataTable, EmptyState, Gauge, IconChevronRight, IconExport,
  IconPlus, IconSearch, Input, ListPane, ManifestPane, PageHeader, Segmented,
  Select, Sheet, Stamp, Toolbar, Workbench, type Column,
} from '~/design-system';
import { businessPath, path } from '~/app/navigation';
import { useBusiness } from '~/platform/tenancy/BusinessProvider';
import { useOrders, useProducts } from '~/platform/data/queries';
import { useHasManifestSpace } from '~/platform/hooks/useMediaQuery';
import {
  ORDER_TONE, isTerminalOrderStatus, orderHandlingClass, orderRowTone,
} from '~/platform/domain/presentation';
import { OrderManifest } from './OrderManifest';
import { orderMeta, selectionStats } from './meta';
import './orders.css';

/* ==================================================================
   THE ORDER BOOK.

   Rebuilt against how the floor actually reads this screen, which the
   old admin got right and a generic CRM table gets wrong:

     - the order number is the handle. It is the loudest thing in the
       row and it is a link, because half of all clicks are "open that
       one", not "look at that one";
     - the client is TWO facts, not one: the trade code the warehouse
       shouts across the floor, and the person who actually rang;
     - money is four columns — total, paid, balance, margin — because
       "who still owes us" is asked of this list every afternoon;
     - selecting rows turns the toolbar into a till roll: count, sold,
       cost, profit, paid, balance.

   Row count is a control, not a constant: 50 for a scan, 500 when
   somebody is reconciling a month.
   ================================================================== */

type DayFilter = 'today' | 'tomorrow' | 'all';
const ROW_COUNTS = [50, 100, 200, 500] as const;

export default function OrdersPage() {
  const { t } = useTranslation(['orders', 'common']);
  const { business, format, can } = useBusiness();
  const navigate = useNavigate();
  const [dayFilter, setDayFilter] = useState<DayFilter>('all');
  const [openOnly, setOpenOnly] = useState(false);
  const [shortOnly, setShortOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState<number>(50);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selection, setSelection] = useState<ReadonlySet<string>>(new Set());
  const hasManifestSpace = useHasManifestSpace();

  const base = businessPath(business.slug);
  const openNewOrder = () => void navigate({ to: path(`${base}/orders/new`) });
  const openOrder = (orderId: string) =>
    void navigate({ to: path(`${base}/orders/${orderId}`) });

  const today = format.tradingDay();
  const tomorrow = addDays(today, 1);

  const filters = useMemo(
    () => ({
      ...(dayFilter === 'today' ? { deliveryDate: today } : {}),
      ...(dayFilter === 'tomorrow' ? { deliveryDate: tomorrow } : {}),
      ...(openOnly ? { statuses: [...OPEN_ORDER_STATUSES] } : {}),
      ...(shortOnly ? { shortOnly: true } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      limit,
    }),
    [dayFilter, today, tomorrow, openOnly, shortOnly, search, limit],
  );

  const { data: orders = [], isLoading, isFetching } = useOrders(business.id, filters);
  const { data: products = [] } = useProducts(business.id);

  const productsById = useMemo(
    () => new Map<string, Product>(products.map((p) => [p.id as string, p])),
    [products],
  );

  const taxBpsFor = useMemo(() => {
    const rates = new Map(business.settings.taxScheme.rates.map((r) => [r.key, r.bps]));
    return (key: string) => rates.get(key) ?? 0;
  }, [business.settings.taxScheme]);

  const money = (minor: number) =>
    format.money({ minor, currency: business.settings.currency }, { bare: true });

  /* ---------------- Columns ---------------- */

  const columns = useMemo<Column<Order>[]>(
    () => [
      {
        /* The handle. Loud, mono, and a real link — middle-click opens
           it in a tab, which is how anybody reconciling a day works. */
        key: 'number',
        header: t('number'),
        width: '124px',
        sortable: true,
        sortValue: (o) => o.number,
        cell: (o) => (
          <a
            className="pd-orders__handle"
            href={`${base}/orders/${o.id}`}
            onClick={(e) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              openOrder(o.id);
            }}
          >
            {o.number}
          </a>
        ),
      },
      {
        /* Trade code first — it is what the floor calls the client. */
        key: 'client',
        header: t('common:labels.customer'),
        width: 'minmax(150px, 1.4fr)',
        sortable: true,
        sortValue: (o) => orderMeta(o).tradeName ?? o.customerName,
        cell: (o) => {
          const meta = orderMeta(o);
          return (
            <div className="pd-orders__client">
              {meta.tradeName ? <Code>{meta.tradeName}</Code> : null}
              <span className="truncate">{o.customerName}</span>
            </div>
          );
        },
      },
      {
        /* Who actually ordered. The name you ask for on the phone. */
        key: 'contact',
        header: t('columns.contact'),
        width: 'minmax(110px, 1fr)',
        hideBelow: 'lg',
        sortable: true,
        sortValue: (o) => orderMeta(o).contactName ?? '',
        cell: (o) => {
          const meta = orderMeta(o);
          if (!meta.contactName) return <span className="faint">—</span>;
          return (
            <div className="pd-orders__contact">
              <span className="truncate">{meta.contactName}</span>
              {meta.phone ? <span className="num faint truncate">{meta.phone}</span> : null}
            </div>
          );
        },
      },
      {
        key: 'window',
        header: t('deliveryDate'),
        width: '132px',
        sortable: true,
        sortValue: (o) => `${o.deliveryDate}${o.deliveryWindow?.from ?? ''}`,
        cell: (o) => (
          <span className="pd-orders__window">
            <span className="num">{format.date(o.deliveryDate, 'weekday')}</span>
            {o.deliveryWindow ? (
              <span className="num faint">{format.timeWindow(o.deliveryWindow)}</span>
            ) : null}
          </span>
        ),
      },
      {
        key: 'lines',
        header: t('detail.lines'),
        align: 'num',
        width: '58px',
        hideBelow: 'sm',
        sortable: true,
        sortValue: (o) => o.lines.length,
        cell: (o) => <span className="num">{o.lines.length}</span>,
      },
      {
        key: 'fill',
        header: t('fillRate'),
        width: '104px',
        hideBelow: 'lg',
        sortable: true,
        sortValue: (o) => computeOrderTotals(o, taxBpsFor).fillRate ?? -1,
        cell: (o) => {
          const { fillRate, shortLineCount } = computeOrderTotals(o, taxBpsFor);
          if (fillRate === null) return <span className="faint">—</span>;
          return (
            <Gauge
              value={fillRate}
              tone={shortLineCount > 0 ? 'warn' : 'ok'}
              label={t('fillRate')}
            />
          );
        },
      },
      {
        key: 'total',
        header: `${t('common:labels.total')} (${format.currencySymbol()})`,
        align: 'num',
        width: '110px',
        sortable: true,
        sortValue: (o) => computeOrderTotals(o, taxBpsFor).gross.minor,
        cell: (o) => (
          <span className="num pd-orders__total">
            {money(computeOrderTotals(o, taxBpsFor).gross.minor)}
          </span>
        ),
      },
      {
        key: 'paid',
        header: t('columns.paid'),
        align: 'num',
        width: '100px',
        hideBelow: 'lg',
        sortable: true,
        sortValue: (o) => orderMeta(o).paidMinor ?? -1,
        cell: (o) => {
          const paid = orderMeta(o).paidMinor;
          if (paid === null) return <span className="faint">—</span>;
          return <span className="num">{money(paid)}</span>;
        },
      },
      {
        /* Green when settled, alert when owed — the afternoon question. */
        key: 'balance',
        header: t('columns.balance'),
        align: 'num',
        width: '104px',
        hideBelow: 'md',
        sortable: true,
        sortValue: (o) => {
          const paid = orderMeta(o).paidMinor;
          if (paid === null) return null;
          return computeOrderTotals(o, taxBpsFor).gross.minor - paid;
        },
        cell: (o) => {
          const paid = orderMeta(o).paidMinor;
          if (paid === null) return <span className="faint">—</span>;
          const balance = computeOrderTotals(o, taxBpsFor).gross.minor - paid;
          return (
            <span className="num pd-orders__balance" data-settled={balance <= 0 || undefined}>
              {money(balance)}
            </span>
          );
        },
      },
      {
        key: 'payment',
        header: t('columns.payment'),
        width: '116px',
        hideBelow: 'lg',
        cell: (o) => {
          const meta = orderMeta(o);
          if (!meta.paymentType && !meta.terminal) return <span className="faint">—</span>;
          return (
            <div className="pd-orders__payment">
              {meta.paymentType ? (
                <span className="t-small truncate">{meta.paymentType}</span>
              ) : null}
              {meta.terminal ? <Code>{meta.terminal}</Code> : null}
            </div>
          );
        },
      },
      {
        key: 'channel',
        header: t('channel'),
        width: '86px',
        hideBelow: 'lg',
        cell: (o) => <span className="t-small dim">{t(`channels.${o.channel}`)}</span>,
      },
      {
        key: 'status',
        header: t('common:labels.status'),
        width: '108px',
        sortable: true,
        sortValue: (o) => o.status,
        cell: (o) => (
          <Stamp
            tone={ORDER_TONE[o.status]}
            weight={isTerminalOrderStatus(o.status) ? 'solid' : 'outline'}
          >
            {t(`status.${o.status}`)}
          </Stamp>
        ),
      },
      {
        /* The explicit way in. A row that only opens on click is a
           guess; this is a button that says what it does. */
        key: 'open',
        header: '',
        width: '40px',
        cell: (o) => (
          <Button
            className="pd-orders__open"
            variant="quiet"
            size="sm"
            iconOnly
            icon={<IconChevronRight />}
            aria-label={t('detail.openFull')}
            title={t('detail.openFull')}
            onClick={() => openOrder(o.id)}
          />
        ),
      },
    ],
    [t, format, taxBpsFor, base],
  );

  /* ---------------- Selection stats ---------------- */

  const selectedOrders = useMemo(
    () => orders.filter((o) => selection.has(o.id as string)),
    [orders, selection],
  );
  const stats = useMemo(
    () => selectionStats(selectedOrders, taxBpsFor),
    [selectedOrders, taxBpsFor],
  );

  const selectedOrder = orders.find((o) => o.id === selectedId) ?? null;

  return (
    <Workbench>
      <PageHeader
        eyebrow={t('eyebrow')}
        title={t('title')}
        count={
          isLoading
            ? undefined
            : `${t('common:labels.results', { count: orders.length })}${isFetching ? ' ·' : ''}`
        }
        actions={
          <>
            <Button variant="quiet" icon={<IconExport />}>
              {t('common:actions.export')}
            </Button>
            {can('order', 'create') ? (
              <Button variant="primary" icon={<IconPlus />} onClick={openNewOrder}>
                {t('newOrder')}
              </Button>
            ) : null}
          </>
        }
      />

      <Toolbar
        trailing={
          <div className="row">
            <Input
              type="search"
              value={search}
              placeholder={t('searchPlaceholder')}
              aria-label={t('common:actions.search')}
              prefix={<IconSearch />}
              onChange={(e) => setSearch(e.target.value)}
              className="pd-orders__search"
            />
            <Select
              value={String(limit)}
              aria-label={t('columns.rows')}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="pd-orders__rows"
            >
              {ROW_COUNTS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </div>
        }
      >
        <Segmented<DayFilter>
          label={t('filters.day')}
          value={dayFilter}
          onChange={setDayFilter}
          options={[
            { value: 'today', label: t('common:labels.today') },
            { value: 'tomorrow', label: t('common:labels.tomorrow') },
            { value: 'all', label: t('common:labels.all') },
          ]}
        />
        <Segmented<'open' | 'all'>
          label={t('filters.status')}
          value={openOnly ? 'open' : 'all'}
          onChange={(v) => setOpenOnly(v === 'open')}
          options={[
            { value: 'open', label: t('filters.openOnly') },
            { value: 'all', label: t('common:labels.all') },
          ]}
        />
        <Button
          variant="quiet"
          size="sm"
          aria-pressed={shortOnly}
          onClick={() => setShortOnly((v) => !v)}
        >
          {t('filters.shortOnly')}
        </Button>
      </Toolbar>

      {/* The till roll. Only present when something is selected — an
          always-on stats bar is chrome nobody reads. */}
      {stats.count > 0 ? (
        <div className="pd-orders__tally" data-print="hide">
          <span className="pd-orders__tally-count">
            {t('common:labels.selected', { count: stats.count })}
          </span>
          <Tally label={t('columns.sold')} value={money(stats.grossMinor)} />
          {stats.cogsMinor !== null ? (
            <Tally label={t('columns.cogs')} value={money(stats.cogsMinor)} />
          ) : null}
          {stats.profitMinor !== null ? (
            <Tally
              label={t('columns.profit')}
              value={money(stats.profitMinor)}
              tone={stats.profitMinor >= 0 ? 'ok' : 'alert'}
              extra={
                stats.netMinor > 0
                  ? `${Math.round((stats.profitMinor / stats.netMinor) * 100)}%`
                  : undefined
              }
            />
          ) : null}
          {stats.paidMinor !== null ? (
            <Tally label={t('columns.paid')} value={money(stats.paidMinor)} />
          ) : null}
          {stats.balanceMinor !== null ? (
            <Tally
              label={t('columns.balance')}
              value={money(stats.balanceMinor)}
              tone={stats.balanceMinor > 0 ? 'alert' : 'ok'}
            />
          ) : null}
          <Button
            variant="quiet"
            size="sm"
            className="pd-orders__tally-clear"
            onClick={() => setSelection(new Set())}
          >
            {t('common:actions.clearFilters')}
          </Button>
        </div>
      ) : null}

      <ListPane>
        <DataTable<Order>
          rows={orders}
          columns={columns}
          rowKey={(o) => o.id}
          rowMeta={(o) => ({
            handlingClass: orderHandlingClass(o, productsById),
            ...(orderRowTone(o) ? { tone: orderRowTone(o) } : {}),
            struck: o.status === 'cancelled',
          })}
          onRowActivate={(o) => setSelectedId(o.id)}
          activeRowKey={selectedId}
          selection={{ selected: selection, onChange: setSelection }}
          loading={isLoading}
          caption={t('title')}
          empty={
            <EmptyState
              title={t('empty')}
              body={t('emptyBody')}
              action={
                can('order', 'create') ? (
                  <Button variant="primary" icon={<IconPlus />} onClick={openNewOrder}>
                    {t('newOrder')}
                  </Button>
                ) : undefined
              }
            />
          }
        />
      </ListPane>

      {/* On a desk the manifest sits beside the list; below 1100px the
          same component is presented as a sheet. One implementation,
          and only ever one of the two mounted. */}
      {/* Keyed by order id so switching rows remounts the manifest and
          its sections cascade in again — opening a different order
          should feel like opening a different order. */}
      {hasManifestSpace ? (
        <ManifestPane open={Boolean(selectedOrder)}>
          {selectedOrder ? (
            <OrderManifest
              key={selectedOrder.id}
              order={selectedOrder}
              productsById={productsById}
              onClose={() => setSelectedId(null)}
              onOpenFull={() => openOrder(selectedOrder.id)}
            />
          ) : null}
        </ManifestPane>
      ) : (
        <Sheet
          open={Boolean(selectedOrder)}
          onOpenChange={(open) => !open && setSelectedId(null)}
          title={selectedOrder ? `${t('number')} ${selectedOrder.number}` : ''}
        >
          {selectedOrder ? (
            <OrderManifest
              key={selectedOrder.id}
              order={selectedOrder}
              productsById={productsById}
              onClose={() => setSelectedId(null)}
              onOpenFull={() => openOrder(selectedOrder.id)}
              embedded
            />
          ) : null}
        </Sheet>
      )}
    </Workbench>
  );
}

function Tally({
  label,
  value,
  tone,
  extra,
}: {
  label: string;
  value: string;
  tone?: 'ok' | 'alert';
  extra?: string | undefined;
}) {
  return (
    <span className="pd-orders__tally-item" data-tone={tone}>
      <span className="stencil">{label}</span>
      <span className="num">
        {value}
        {extra ? <span className="pd-orders__tally-extra">{extra}</span> : null}
      </span>
    </span>
  );
}

const addDays = (plain: string, days: number): string => {
  const at = new Date(`${plain}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
};
